/**
 * POS Products Filters Component
 *
 * Filters bar for the POS products list.
 */

import { Search } from 'lucide-react';
import { Input } from '@/components/ui';
import { Select } from '@/components/ui';
import { Checkbox } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { CategoryCount } from '../services/pos-products.service';

type ProductType = 'PRODUCT' | 'OPTION' | null;

interface PosProductsFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  showMissingRecipesOnly: boolean;
  onShowMissingRecipesChange: (value: boolean) => void;
  type: ProductType;
  onTypeChange: (type: ProductType) => void;
  category: string | null;
  onCategoryChange: (category: string | null) => void;
  categories: CategoryCount[];
  isCategoriesLoading?: boolean;
  onReset: () => void;
}

const typeOptions = [
  { value: '', label: 'Tous' },
  { value: 'PRODUCT', label: 'Produits' },
  { value: 'OPTION', label: 'Options' },
] as const;

export function PosProductsFilters({
  search,
  onSearchChange,
  showMissingRecipesOnly,
  onShowMissingRecipesChange,
  type,
  onTypeChange,
  category,
  onCategoryChange,
  categories,
  isCategoriesLoading,
  onReset,
}: PosProductsFiltersProps) {
  const hasActiveFilters = search || showMissingRecipesOnly || type || category;

  // Build category options for the select
  const categoryOptions = [
    { value: '', label: 'Toutes les categories' },
    ...categories.map((cat) => ({
      value: cat.name,
      label: `${cat.name} (${cat.count})`,
    })),
  ];

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search */}
        <div className="flex-1 min-w-[250px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher un produit POS..."
            className="pl-9"
          />
        </div>

        {/* Type filter - Pills */}
        <div className="flex items-center gap-1 bg-neutral-100 p-1 rounded-lg">
          {typeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => onTypeChange(option.value === '' ? null : option.value as ProductType)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                (type === null && option.value === '') || type === option.value
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-800'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Category filter - Dropdown */}
        <div className="min-w-[200px]">
          <Select
            value={category || ''}
            onChange={(value) => onCategoryChange(value || null)}
            options={categoryOptions}
            disabled={isCategoriesLoading}
          />
        </div>

        {/* Missing recipes filter */}
        <label className="flex items-center gap-3 px-4 py-2 bg-neutral-50 rounded-xl cursor-pointer hover:bg-neutral-100 transition-colors">
          <Checkbox
            checked={showMissingRecipesOnly}
            onChange={(e) => onShowMissingRecipesChange(e.target.checked)}
          />
          <span className="text-sm text-dark-500 whitespace-nowrap">
            Recettes manquantes uniquement
          </span>
        </label>

        {/* Reset */}
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors"
          >
            Reinitialiser
          </button>
        )}
      </div>
    </div>
  );
}
